import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Product } from '@haccp/api-client';
import { AuthenticatedImage } from '@/components/ui';

const XIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const TrashIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const CalendarIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const UserIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

export function ProductDetail({
  product,
  onClose,
  onDelete,
  isDeleting,
  canDelete,
}: {
  product: Product;
  onClose: () => void;
  onDelete: (productId: string) => void;
  isDeleting: boolean;
  canDelete: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const expiryDate = product.expiryDate ? new Date(product.expiryDate) : null;
  const createdDate = new Date(product.createdAt);

  const today = new Date();
  const hasValidExpiry = !!expiryDate && !Number.isNaN(expiryDate.getTime());
  const hasNoExpiry = hasValidExpiry && expiryDate.getFullYear() >= 9999;
  const daysUntilExpiry = hasValidExpiry && !hasNoExpiry
    ? Math.floor((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    : 0;
  const isExpired = hasValidExpiry && !hasNoExpiry && daysUntilExpiry < 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex flex-col items-center justify-start sm:justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full my-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between p-6 border-b border-slate-200 bg-white">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{product.name}</h2>
            <p className="text-sm text-slate-600">{t('traceability.supplierFrom', { supplier: product.supplier })}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-100 rounded-lg transition"
          >
            <XIcon className="w-6 h-6 text-slate-600" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {product.photos && product.photos.length > 0 && (
            <div>
              <h3 className="font-semibold text-slate-900 mb-3">{t('traceability.photos')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {product.photos.map((photo) => (
                  <div key={photo.id} className="aspect-square rounded-lg overflow-hidden bg-slate-100">
                    <AuthenticatedImage
                      src={photo.photoUrl}
                      alt="Product"
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {product.classification && (
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
              <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">
                {t('traceability.classification')}
              </span>
              <span className="text-sm font-semibold text-blue-800 capitalize">
                {t(`traceability.classification_${product.classification}`, { defaultValue: product.classification })}
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm font-medium text-slate-600 mb-1">{t('traceability.batchNumber')}</p>
              <p className="text-lg font-mono text-slate-900">{product.batchNumber}</p>
            </div>

            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm font-medium text-slate-600 mb-1">{t('traceability.quantity')}</p>
              <p className="text-lg text-slate-900 font-semibold">
                {product.quantity} {product.quantityUnit}
              </p>
            </div>

            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm font-medium text-slate-600 mb-1">{t('traceability.storageLocation')}</p>
              <p className="text-lg text-slate-900">{product.storageLocation}</p>
            </div>

            <div className={`rounded-lg p-4 ${isExpired ? 'bg-red-50' : 'bg-green-50'}`}>
              <div className="flex items-center gap-2 mb-1">
                <CalendarIcon className={`w-4 h-4 ${isExpired ? 'text-red-600' : 'text-green-600'}`} />
                <p className={`text-sm font-medium ${isExpired ? 'text-red-700' : 'text-green-700'}`}>
                  {t('traceability.expiryDate')}
                </p>
              </div>
              <p className={`text-lg font-semibold ${isExpired ? 'text-red-900' : 'text-green-900'}`}>
                {hasValidExpiry && !hasNoExpiry
                  ? expiryDate.toLocaleDateString(i18n.language)
                  : t('traceability.noExpiry')}
              </p>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4 space-y-3">
            {product.creator && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <UserIcon className="w-4 h-4" />
                <span>
                  {t('traceability.createdBy')}{' '}
                  <span className="font-medium text-slate-900">
                    {product.creator.displayName || product.creator.email}
                  </span>
                </span>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <CalendarIcon className="w-4 h-4" />
              <span>
                {t('traceability.createdAt')}{' '}
                <span className="font-medium text-slate-900">
                  {createdDate.toLocaleDateString(i18n.language)} {t('traceability.at')}{' '}
                  {createdDate.toLocaleTimeString(i18n.language, {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </span>
            </div>
          </div>

          {canDelete && (
            <div className="border-t border-slate-200 pt-4">
              {!showConfirmDelete ? (
                <button
                  onClick={() => setShowConfirmDelete(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition"
                >
                  <TrashIcon className="w-5 h-5" />
                  {t('traceability.deleteProduct')}
                </button>
              ) : (
                <div className="space-y-3 p-4 bg-red-50 rounded-lg">
                  <p className="text-sm font-medium text-red-900">
                    {t('traceability.confirmDelete')}
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowConfirmDelete(false)}
                      className="flex-1 px-3 py-2 bg-white border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition"
                    >
                      {t('traceability.cancel')}
                    </button>
                    <button
                      onClick={() => onDelete(product.id)}
                      disabled={isDeleting}
                      className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isDeleting ? t('traceability.deleting') : t('traceability.delete')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
